import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';
import type { LocaleSettings } from '@/lib/localeFormatters';
import { cn } from '@/lib/utils';
import { Calendar } from 'lucide-react';

export interface DateInputProps {
  label?: string;
  /** ISO date string (YYYY-MM-DD) */
  value: string;
  /** Called with ISO date string (YYYY-MM-DD) or empty string */
  onChange: (isoDate: string) => void;
  error?: string;
  hint?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/** Parse an ISO date string to { day, month, year } or null */
function parseISO(iso: string): { day: string; month: string; year: string } | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { year: m[1], month: m[2], day: m[3] };
}

/** Format date parts according to locale settings */
function formatForDisplay(day: string, month: string, year: string, settings: LocaleSettings): string {
  switch (settings.dateFormat) {
    case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
    case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
    case 'DD.MM.YYYY':
    default: return `${day}.${month}.${year}`;
  }
}

/** Get placeholder string for the current locale format */
function getPlaceholder(settings: LocaleSettings): string {
  switch (settings.dateFormat) {
    case 'MM/DD/YYYY': return 'MM/DD/YYYY';
    case 'YYYY-MM-DD': return 'YYYY-MM-DD';
    case 'DD/MM/YYYY': return 'DD/MM/YYYY';
    case 'DD.MM.YYYY':
    default: return 'DD.MM.YYYY';
  }
}

/** Parse a user-typed display string back to ISO, or null if invalid */
function parseDisplay(text: string, settings: LocaleSettings): string | null {
  let day: string, month: string, year: string;

  if (settings.dateFormat === 'MM/DD/YYYY') {
    const m = text.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
    if (!m) return null;
    [, month, day, year] = m;
  } else if (settings.dateFormat === 'YYYY-MM-DD') {
    const m = text.match(/^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/);
    if (!m) return null;
    [, year, month, day] = m;
  } else {
    // DD.MM.YYYY or DD/MM/YYYY
    const m = text.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
    if (!m) return null;
    [, day, month, year] = m;
  }

  const d = parseInt(day, 10);
  const mo = parseInt(month, 10);
  const y = parseInt(year, 10);
  if (d < 1 || d > 31 || mo < 1 || mo > 12 || y < 1900 || y > 2100) return null;

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

/**
 * A locale-aware date input that displays dates in the app's configured format
 * (e.g. DD.MM.YYYY) while sending ISO strings (YYYY-MM-DD) to the parent.
 *
 * Combines a text input for locale-formatted display/manual entry with a
 * native date input overlay on the calendar icon for picker support.
 */
export function DateInput({ label, value, onChange, error, hint, placeholder, disabled, className }: DateInputProps) {
  const localeSettings = useLocaleSettings();
  const [displayValue, setDisplayValue] = useState('');
  const nativeRef = useRef<HTMLInputElement>(null);
  const inputId = React.useId();

  // Sync display value when the ISO value changes externally
  useEffect(() => {
    const parts = parseISO(value);
    if (parts) {
      setDisplayValue(formatForDisplay(parts.day, parts.month, parts.year, localeSettings));
    } else {
      setDisplayValue('');
    }
  }, [value, localeSettings]);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setDisplayValue(text);

    if (text === '') {
      onChange('');
      return;
    }

    const iso = parseDisplay(text, localeSettings);
    if (iso) {
      onChange(iso);
    }
  }, [onChange, localeSettings]);

  const handleBlur = useCallback(() => {
    if (displayValue === '') {
      onChange('');
      return;
    }
    const iso = parseDisplay(displayValue, localeSettings);
    if (iso) {
      onChange(iso);
      const parts = parseISO(iso);
      if (parts) setDisplayValue(formatForDisplay(parts.day, parts.month, parts.year, localeSettings));
    } else {
      // Revert to previous valid value
      const parts = parseISO(value);
      setDisplayValue(parts ? formatForDisplay(parts.day, parts.month, parts.year, localeSettings) : '');
    }
  }, [displayValue, value, onChange, localeSettings]);

  const handleNativeDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  }, [onChange]);

  const hasError = Boolean(error);

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-foreground mb-1">
          {label}
        </label>
      )}

      <div className="relative">
        {/* Text input for locale-formatted display */}
        <input
          id={inputId}
          type="text"
          value={displayValue}
          onChange={handleTextChange}
          onBlur={handleBlur}
          placeholder={placeholder || getPlaceholder(localeSettings)}
          disabled={disabled}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background px-3 pr-10 py-2 text-sm font-sans',
            'ring-offset-background placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            hasError && 'border-error-500 focus-visible:ring-error-500',
          )}
        />

        {/* Full-width native date input for picker alignment (no mouse capture) */}
        <input
          ref={nativeRef}
          type="date"
          value={value}
          onChange={handleNativeDateChange}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden="true"
          className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
        />

        {/*
         * Clickable calendar icon that opens the native picker.
         * W2-I a11y fix: aria-hidden="true" removes this from the accessibility
         * tree. The button is tabIndex={-1} (mouse-only convenience) — keyboard
         * and screen-reader users interact with the text input above, which is
         * the labelled, keyboard-accessible form control.
         */}
        <button
          type="button"
          onClick={() => nativeRef.current?.showPicker?.()}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden="true"
          className="absolute right-0 top-0 h-full w-10 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
        >
          <Calendar className="w-4 h-4" />
        </button>
      </div>

      {hint && !error && (
        <p id={`${inputId}-hint`} className="mt-1 text-sm text-muted-foreground">{hint}</p>
      )}
      {error && (
        <p id={`${inputId}-error`} className="mt-1 text-sm text-error-600">{error}</p>
      )}
    </div>
  );
}
